import { useMutation, useQueryClient } from '@tanstack/react-query'
import { MessageIcon, Modal, PlusIcon } from '@viapeople/components'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'
import type { CalibrationParticipant } from '../types'

interface CalibrationOverralCommentsRowProps {
	participants: CalibrationParticipant[]
	rowType: string
}

export function CalibrationOverralCommentsRow({ participants, rowType }: CalibrationOverralCommentsRowProps) {
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [selectedComment, setSelectedComment] = useState<{
		participant: CalibrationParticipant
		comment: string
		commentId?: string
	} | null>(null)
	const [editedComment, setEditedComment] = useState('')
	const { sessionId } = useParams<{ sessionId: string }>()
	const { showToast } = useToast()
	const queryClient = useQueryClient()

	// @ts-expect-error - TRPC mutation options type compatibility issue
	const updateCommentMutation = useMutation({
		...trpc.client.calibrations.updateComment.mutationOptions(),
		onSuccess: () => {
			showToast('Comment updated successfully.')
			// Invalidate and refetch the calibration data to update the UI
			queryClient.invalidateQueries({
				queryKey: ['calibrations', 'getData', { sessionId: sessionId || '' }],
			})
			handleCloseModal()
		},
		onError: (error: unknown) => {
			showToast('Failed to update comment.', 'error')
			console.error('Error updating comment:', error)
		},
	})

	// Map rowType to commentType
	const getCommentType = (rowType: string): 'accomplishments' | 'strengths' | 'improvements' => {
		const mapping: Record<string, 'accomplishments' | 'strengths' | 'improvements'> = {
			Accomplishments: 'accomplishments',
			Strengths: 'strengths',
			Improvements: 'improvements',
			'Display Integrity': 'improvements', // Keep existing mapping for backwards compatibility
		}
		return mapping[rowType] || 'improvements'
	}

	const renderCommentCell = (participant: CalibrationParticipant) => {
		// Check if this participant has a comment for this row type
		const commentType = getCommentType(rowType)
		const hasComment = participant.calibrationComments?.some((comment) => comment.commentType === commentType) || false

		if (hasComment) {
			const existingComment = participant.calibrationComments?.find((comment) => comment.commentType === commentType)
			return (
				<MessageIcon
					size={20}
					className='cursor-pointer hover:text-primary-600 transition-colors'
					onClick={() => handleViewComment(participant, existingComment?.content || '', existingComment?.id)}
				/>
			)
		} else {
			return (
				<button
					type='button'
					className='flex items-center gap-2 cursor-pointer hover:text-primary-600 transition-colors'
					onClick={() => handleAddComment(participant.id, rowType)}>
					<PlusIcon size={16} />
					<span className='text-sm text-neutral-600'>Add comment</span>
				</button>
			)
		}
	}

	const handleViewComment = (participant: CalibrationParticipant, comment: string, commentId?: string) => {
		setSelectedComment({ participant, comment, commentId })
		setEditedComment(comment)
		setIsModalOpen(true)
	}

	const handleCloseModal = () => {
		setIsModalOpen(false)
		setSelectedComment(null)
		setEditedComment('')
	}

	const handleSaveComment = async () => {
		if (!selectedComment || !sessionId || !editedComment.trim()) {
			console.error('Missing required data for comment update')
			return
		}

		const commentType = getCommentType(rowType)

		try {
			await updateCommentMutation.mutateAsync({
				sessionId,
				participantId: selectedComment.participant.participantId,
				commentType,
				content: editedComment.trim(),
			})
		} catch (error) {
			console.error('Failed to save comment:', error)
		}
	}

	const handleAddComment = (participantId: string, _rowType: string) => {
		const participant = participants.find((p) => p.id === participantId)
		if (participant) {
			setSelectedComment({ participant, comment: '', commentId: undefined })
			setEditedComment('')
			setIsModalOpen(true)
		}
	}

	return (
		<>
			<div className='w-full flex items-center'>
				<div className='w-80 p-4 border-t border-neutral-200 flex-shrink-0'>{rowType}</div>
				{participants.map((participant, _index) => (
					<div key={participant.id} className='w-48 p-4 border-t border-neutral-200 flex-shrink-0 flex justify-center'>
						{renderCommentCell(participant)}
					</div>
				))}
			</div>

			<Modal
				isOpen={isModalOpen}
				onClose={handleCloseModal}
				title={`${rowType} - ${selectedComment?.participant.firstName} ${selectedComment?.participant.lastName}`}
				buttons={[
					{
						children: 'Cancel',
						variant: 'secondary',
						onClick: handleCloseModal,
					},
					{
						children: updateCommentMutation.isPending ? 'Saving...' : 'Save',
						variant: 'primary',
						onClick: handleSaveComment,
						disabled: updateCommentMutation.isPending || !editedComment.trim(),
					},
				]}>
				<div className='space-y-4'>
					<div>
						<label htmlFor='comment-textarea' className='block text-sm font-medium text-neutral-700 mb-2'>
							Comment
						</label>
						<textarea
							id='comment-textarea'
							value={editedComment}
							onChange={(e) => setEditedComment(e.target.value)}
							className='w-full min-h-32 p-3 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-vertical'
							placeholder='Enter your comment here...'
							rows={6}
						/>
					</div>
				</div>
			</Modal>
		</>
	)
}
